import { useCallback, useMemo } from 'react';
import { SortState, RowData } from '../types';

interface UseSortingProps {
  propsSortState: SortState | undefined;
  structure: any[];
  rowData: RowData[];
  onSortChange?: (newSortState: SortState) => void;
}

/**
 * 2つの項目が一致するかチェック（nullも考慮）
 */
const isDetailMatch = (
  id1: number | null,
  id2: number | null
): boolean => {
  return id1 === id2 || (id1 == null && id2 == null);
};

/**
 * ソート処理用のカスタムフック
 */
export const useSorting = ({
  propsSortState,
  structure,
  rowData,
  onSortChange
}: UseSortingProps) => {
  // フィルター後のデータに合わせてソート状態を調整
  const sortState = useMemo(() => {
    const baseSortState = propsSortState || {
      columnIndex: null,
      direction: null,
      targetDetail: null
    };

    // targetDetailが存在し、フィルター後のstructureに存在する場合は新しいcolumnIndexを見つける
    if (baseSortState.targetDetail && baseSortState.direction) {
      const targetDetail = baseSortState.targetDetail;
      const newColumnIndex = structure.findIndex(item => {
        return (
          isDetailMatch(item.product_large_category_id, targetDetail.product_large_category_id) &&
          isDetailMatch(item.product_medium_category_id, targetDetail.product_medium_category_id) &&
          isDetailMatch(item.product_small_category_id, targetDetail.product_small_category_id) &&
          isDetailMatch(item.product_detail_category_id, targetDetail.product_detail_category_id)
        );
      });

      if (newColumnIndex >= 0) {
        // ソート対象の列が存在する場合は新しいcolumnIndexで更新
        return {
          ...baseSortState,
          columnIndex: newColumnIndex
        };
      } else {
        // ソート対象の列が存在しない場合はソートをクリア
        return {
          columnIndex: null,
          direction: null,
          targetDetail: null
        };
      }
    }

    return baseSortState;
  }, [propsSortState, structure]);

  // 細項目のクリックハンドラー（ソート切り替え）
  const handleDetailColumnClick = useCallback((columnIndex: number) => {
    const targetStructureItem = structure[columnIndex];
    if (!targetStructureItem) return;

    const targetDetail = {
      product_large_category_id: targetStructureItem.product_large_category_id,
      product_medium_category_id: targetStructureItem.product_medium_category_id,
      product_small_category_id: targetStructureItem.product_small_category_id,
      product_detail_category_id: targetStructureItem.product_detail_category_id
    };

    let newSortState;
    // 同じ列をクリックした場合：desc → asc → null → desc の順で切り替え
    if (sortState.columnIndex === columnIndex) {
      if (sortState.direction === 'desc') {
        newSortState = { columnIndex, direction: 'asc' as const, targetDetail };
      } else if (sortState.direction === 'asc') {
        newSortState = { columnIndex: null, direction: null, targetDetail: null };
      } else {
        newSortState = { columnIndex, direction: 'desc' as const, targetDetail };
      }
    }
    // 別の列をクリックした場合：その列を降順でソート開始
    else {
      newSortState = { columnIndex, direction: 'desc' as const, targetDetail };
    }

    // 親コンポーネントに通知
    if (onSortChange) {
      onSortChange(newSortState);
    }
  }, [sortState, onSortChange, structure]);

  // ソートされたデータを取得
  const getSortedData = useCallback(() => {
    if (!sortState.direction || !sortState.targetDetail) {
      return rowData;
    }

    // targetDetailを使用してソート
    const targetDetail = sortState.targetDetail;

    const sortedData = [...rowData].sort((a, b) => {
      // 指定された列のレベル値を取得
      const aLevel = a.levels.find(level => {
        return (
          isDetailMatch(level.product_large_category_id, targetDetail.product_large_category_id) &&
          isDetailMatch(level.product_medium_category_id, targetDetail.product_medium_category_id) &&
          isDetailMatch(level.product_small_category_id, targetDetail.product_small_category_id) &&
          isDetailMatch(level.product_detail_category_id, targetDetail.product_detail_category_id)
        );
      });
      const bLevel = b.levels.find(level => {
        return (
          isDetailMatch(level.product_large_category_id, targetDetail.product_large_category_id) &&
          isDetailMatch(level.product_medium_category_id, targetDetail.product_medium_category_id) &&
          isDetailMatch(level.product_small_category_id, targetDetail.product_small_category_id) &&
          isDetailMatch(level.product_detail_category_id, targetDetail.product_detail_category_id)
        );
      });

      const aValue = aLevel ? aLevel.product_level_value : 0;
      const bValue = bLevel ? bLevel.product_level_value : 0;

      // 降順の場合は大きい順、昇順の場合は小さい順
      if (sortState.direction === 'desc') {
        return bValue - aValue;
      } else {
        return aValue - bValue;
      }
    });

    return sortedData;
  }, [rowData, sortState]);

  return {
    sortState,
    handleDetailColumnClick,
    getSortedData
  };
};
